import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../config/config.json';
import toast from 'react-hot-toast';

export interface CartItem {
  id: number;
  name: string;
  quantity: number;
  fiat_price: string;
  fg_price: string;
  image_path: string;
  status: string;
  suffix: string;
  metadata?: any;
  stats_description: string;
}

interface CartItemsState {
  items: CartItem[];
  isLoading: boolean;
  error: string | null;
}

const initialState: CartItemsState = {
  items: [],
  isLoading: false,
  error: null,
};

const cartItemsSlice = createSlice({
  name: 'cartItems',
  initialState,
  reducers: {
    setCartItems: (state, action: PayloadAction<CartItem[]>) => {
      state.items = action.payload;
    },
    addCartItem: (state, action: PayloadAction<CartItem>) => {
      state.items.push(action.payload);
    },
    removeCartItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    clearCartItems: (state) => {
      state.items = [];
    },
  },
});

export const { setCartItems, addCartItem, removeCartItem, clearCartItems } = cartItemsSlice.actions;
export default cartItemsSlice.reducer;

// TanStack Query hooks
export function useCartItems() {
  const sessionToken = localStorage.getItem('session_token');
  return useQuery({
    queryKey: ['cartItems'],
    queryFn: async () => {
      const response = await axios.post(
        `${config.backend_base_url}cart/items/all`,
        { term: '', quick_cart: 1 },
        { headers: { 'session-token': sessionToken } }
      );
      return response.data || [];
    },
    enabled: !!sessionToken,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
  });
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');
  return useMutation({
    mutationFn: async (itemId: number) => {
      await axios.post(
        `${config.backend_base_url}cart/remove`,
        { item_ids: [itemId] },
        { headers: { 'session-token': sessionToken } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartItems'] });
      toast.success('Item removed from cart');
    },
    onError: () => {
      toast.error('Failed to remove item from cart');
    },
  });
}
